import type { SupabaseClient } from '@supabase/supabase-js';
import { ensureSession } from './supabase';
import { NET_TIMEOUT_MS, withTimeout } from './timeout';

export interface LeaderboardRow {
  rank: number;
  userId: string;
  pseudo: string;
  score: number;
  outcome: 'victory' | 'died' | 'cleared';
  kills: number;
  durationMs: number;
  roomsCleared: number;
  createdAt: string;
  day?: string;
}

export interface LeaderboardApi {
  daily(day: string): Promise<LeaderboardRow[]>;
  global(): Promise<LeaderboardRow[]>;
  mine(day: string): Promise<{ daily: LeaderboardRow | null; global: LeaderboardRow | null }>;
  setPseudo(pseudo: string): Promise<string>;
  pseudo(): Promise<string>;
  dailySeed(day: string): Promise<number>;
}

const PAGE = 50;
const DEFAULT_PSEUDO = 'Songeur';
const PSEUDO_RANGE_MESSAGE = 'Pseudo : 2 à 16 caractères';

/** Date locale `YYYY-MM-DD` (et non UTC : le « jour » du joueur est celui de son horloge). */
export function todayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** `null` si le pseudo est valide (après trim : 2 à 16 caractères, sur une seule ligne), sinon un message français. */
export function validatePseudo(p: string): string | null {
  const t = p.trim();
  if (/[\r\n]/.test(t)) return 'Pseudo : une seule ligne';
  const len = [...t].length;
  return len < 2 || len > 16 ? PSEUDO_RANGE_MESSAGE : null;
}

/** `pinned` vaut `mine` quand la ligne du joueur n'apparaît pas dans `rows`, `null` sinon. */
export function pinMine<T extends { userId: string }>(rows: T[], mine: T | null): { rows: T[]; pinned: T | null } {
  return { rows, pinned: mine && !rows.some((r) => r.userId === mine.userId) ? mine : null };
}

function toRow(raw: Record<string, unknown>): LeaderboardRow {
  const row: LeaderboardRow = {
    rank: Number(raw.rank),
    userId: String(raw.user_id),
    pseudo: String(raw.pseudo),
    score: Number(raw.score),
    outcome: raw.outcome as LeaderboardRow['outcome'],
    kills: Number(raw.kills),
    durationMs: Number(raw.duration_ms),
    roomsCleared: Number(raw.rooms_cleared),
    createdAt: String(raw.created_at),
  };
  if (typeof raw.day === 'string') row.day = raw.day;
  return row;
}

export function createLeaderboardApi(client: SupabaseClient): LeaderboardApi {
  async function top(view: 'leaderboard_daily' | 'leaderboard_global', day?: string): Promise<LeaderboardRow[]> {
    const base = client.from(view).select('*');
    const { data, error } = await (day === undefined ? base : base.eq('day', day)).order('rank').limit(PAGE);
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(toRow);
  }

  async function ownRow(view: 'leaderboard_daily' | 'leaderboard_global', uid: string, day?: string): Promise<LeaderboardRow | null> {
    const base = client.from(view).select('*').eq('user_id', uid);
    const { data, error } = await (day === undefined ? base : base.eq('day', day)).maybeSingle();
    if (error) throw error;
    return data ? toRow(data as Record<string, unknown>) : null;
  }

  // Chaque appel est borné à NET_TIMEOUT_MS : au-delà, il échoue comme une erreur réseau (la page affiche son état d'erreur).
  const bounded = <T>(label: string, fn: () => Promise<T>): Promise<T> => withTimeout(fn(), NET_TIMEOUT_MS, label);

  return {
    daily: (day) => bounded('classement du jour', () => top('leaderboard_daily', day)),
    global: () => bounded('classement global', () => top('leaderboard_global')),
    mine: (day) => bounded('ma ligne de classement', async () => {
      const uid = await ensureSession(client);
      if (!uid) return { daily: null, global: null };
      const [daily, global] = await Promise.all([ownRow('leaderboard_daily', uid, day), ownRow('leaderboard_global', uid)]);
      return { daily, global };
    }),
    async pseudo() {
      try {
        return await bounded('pseudo', async () => {
          const uid = await ensureSession(client);
          if (!uid) return DEFAULT_PSEUDO;
          const { data, error } = await client.from('rogfus_profiles').select('pseudo').eq('id', uid).maybeSingle();
          const pseudo = (data as { pseudo?: unknown } | null)?.pseudo;
          return error || typeof pseudo !== 'string' || pseudo === '' ? DEFAULT_PSEUDO : pseudo;
        });
      } catch { return DEFAULT_PSEUDO; }
    },
    async setPseudo(pseudo) {
      const invalid = validatePseudo(pseudo);
      if (invalid) throw new Error(invalid);
      const trimmed = pseudo.trim();
      return bounded('enregistrement du pseudo', async () => {
        const uid = await ensureSession(client);
        if (!uid) throw new Error('Connexion indisponible : impossible d\'enregistrer le pseudo');
        const { error } = await client.from('rogfus_profiles').upsert({ id: uid, pseudo: trimmed });
        if (error) throw error.code === '23514' ? new Error(PSEUDO_RANGE_MESSAGE) : error;
        return trimmed;
      });
    },
    dailySeed: (day) => bounded('seed du jour', async () => {
      const { data, error } = await client.rpc('rogfus_daily_seed', { d: day });
      if (error) throw error;
      return Number(data) >>> 0;
    }),
  };
}
